import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { Settings, BarChart3, Download, Code, Trash2, FileEdit } from 'lucide-react';
import { VideoCardUnified } from '@/components/shared/VideoCardUnified';
import { DeleteVideoModal } from '@/components/shared/DeleteVideoModal';
import { RenameVideoModal } from '@/components/shared/RenameVideoModal';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { downloadVideo } from '@/utils/downloadVideo';
import { toast } from 'sonner';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { useAuth } from '@/hooks/useAuth';

interface DraggableVideoCardProps {
  id: string;
  title: string;
  thumbnail: string;
  viewCount: number;
  isProcessing?: boolean;
  isNew?: boolean;
  folderId?: string | null;
  status?: string;
  createdAt?: string;
  uploadDate?: string;
  onSelect: () => void;
}

export function DraggableVideoCard({
  id,
  title,
  thumbnail,
  viewCount,
  isProcessing,
  isNew,
  folderId,
  status,
  createdAt,
  uploadDate,
  onSelect
}: DraggableVideoCardProps) {
  useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [copyCodeModalOpen, setCopyCodeModalOpen] = useState(false);
  const videos = useVideoDashboardStore(state => state.videos);
  const updateVideo = useVideoDashboardStore(state => state.updateVideo);
  
  const currentVideo = videos.find(v => v.id === id);
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: {
      type: 'video',
      videoId: id,
      folderId
    }
  });

  const videoIsProcessing = isProcessing || status === 'processing';

  return (
    <>
    <VideoCardUnified
      id={id}
      title={title}
      thumbnail={thumbnail}
      variant="dashboard"
      viewCount={viewCount}
      isProcessing={videoIsProcessing}
      isDragging={isDragging}
      badge={
        videoIsProcessing
          ? { label: translateInterfaceText("Processando"), variant: 'processing' }
          : isNew
          ? { label: translateInterfaceText("Novo"), variant: 'new' }
          : undefined
      }
      actions={[
        {
          label: translateInterfaceText("Personalizar"),
          icon: <Settings className="h-4 w-4" />,
          onClick: () => {
            console.log('Configurar Vídeo clicked, navigating to:', `/personalizations/${id}`);
            navigate(`/personalizations/${id}`);
          }
        },
        {
          label: translateInterfaceText("Renomear"),
          icon: <FileEdit className="h-4 w-4" />,
          onClick: () => setRenameModalOpen(true)
        },
        {
          label: translateInterfaceText("Ver Métricas"),
          icon: <BarChart3 className="h-4 w-4" />,
          onClick: () => navigate(`/metricas/${id}`)
        },
        {
          label: translateInterfaceText("Download"),
          icon: <Download className="h-4 w-4" />,
          onClick: () => {
            if (currentVideo?.videoUrl) {
              downloadVideo(currentVideo.videoUrl, title);
            } else {
              toast.error(translateInterfaceText("URL do vídeo não disponível"));
            }
          }
        },
        {
          label: translateInterfaceText("Copiar código"),
          icon: <Code className="h-4 w-4" />,
          onClick: () => setCopyCodeModalOpen(true)
        },
        {
          label: translateInterfaceText("Deletar"),
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => setDeleteModalOpen(true),
          variant: 'destructive' as const
        }
      ]}
      onClick={videoIsProcessing ? undefined : onSelect}
      dragRef={setNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      dragTransform={transform}
    />
    
    {/* Modal de confirmação de exclusão */}
    <DeleteVideoModal
      open={deleteModalOpen}
      onOpenChange={setDeleteModalOpen}
      videoTitle={title}
      onConfirm={async () => {
        // Implementar lógica de exclusão aqui
        toast.success(translateInterfaceText("Vídeo excluído com sucesso"));
      }}
    />
    
    {/* Modal de renomear */}
    <RenameVideoModal
      open={renameModalOpen}
      onOpenChange={setRenameModalOpen}
      currentTitle={title}
      onConfirm={async (newTitle) => {
        updateVideo(id, { title: newTitle });
        toast.success(translateInterfaceText("Vídeo renomeado com sucesso"));
      }}
    />
    
    {/* Modal de copiar código */}
    <CopyCodeModal
      isOpen={copyCodeModalOpen}
      onClose={() => setCopyCodeModalOpen(false)}
      entityType="video"
      entityId={id}
      entityName={title}
      userId={user?.id_user}
      videoOrigin="ORIGINAL"
    />
    </>
  );
}
